import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { Environment } from "../../environment";
import { AccountService } from "./account.service";

interface BrowserSpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    [index: number]: { transcript: string };
}

interface BrowserSpeechRecognitionResultList {
    readonly length: number;
    [index: number]: BrowserSpeechRecognitionResult;
}

interface BrowserSpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: BrowserSpeechRecognitionResultList;
}

interface BrowserSpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message?: string;
}

interface BrowserSpeechRecognition extends EventTarget {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    maxAlternatives: number;
    onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
    onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

interface BrowserSpeechRecognitionConstructor {
    new(): BrowserSpeechRecognition;
}

interface BrowserWindow extends Window {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
}

interface WavFormat {
    audioFormat: number;
    channelCount: number;
    sampleRate: number;
    bitsPerSample: number;
    blockAlign: number;
    dataOffset: number;
}

interface ActiveStreamingPlayback {
    audioContext: AudioContext;
    gainNode: GainNode;
    sourceNodes: Set<AudioBufferSourceNode>;
    scheduledTime: number;
    pendingSourceCount: number;
    streamCompleted: boolean;
}

type ByteArray = Uint8Array<ArrayBufferLike>;

@Injectable({
    providedIn: "root",
})
export class VoiceService {
    private recognition?: BrowserSpeechRecognition;
    private recordingSubject?: Subject<string>;
    private shouldKeepRecording = false;
    private recordingLanguage = "en-US";
    private committedTranscript = "";
    private sessionResults: Array<{ transcript: string; isFinal: boolean }> = [];
    private lastEmittedTranscript = "";
    private restartTimeoutId?: number;
    private activeReadTextResolver?: () => void;
    private activePlaybackAbortController?: AbortController;
    private activeStreamingPlayback?: ActiveStreamingPlayback;
    private playbackRequestId = 0;
    private readonly minStreamingFrames = 4096;
    private readonly restartDelayMs = 100;

    public constructor(private accountService: AccountService) {
    }

    public startRecording(language = "en-US"): Observable<string> {
        if (this.recordingSubject && this.shouldKeepRecording) {
            return this.recordingSubject.asObservable();
        }

        const speechRecognitionConstructor = this.getSpeechRecognitionConstructor();
        if (!speechRecognitionConstructor) {
            return new Observable<string>((observer) => {
                observer.error(new Error("Speech recognition is not supported by this browser."));
            });
        }

        this.forceStopCurrentRecognition();

        this.shouldKeepRecording = true;
        this.recordingLanguage = language;
        this.committedTranscript = "";
        this.resetSessionResults();
        this.lastEmittedTranscript = "";
        this.recordingSubject = new Subject<string>();

        try {
            this.startRecognitionSession();
        } catch (error) {
            this.emitRecordingError(this.asError(error, "Unable to start speech recognition."));
        }

        return this.recordingSubject.asObservable();
    }

    public stopRecording(): void {
        this.shouldKeepRecording = false;

        if (!this.recognition) {
            this.completeRecording();
            return;
        }

        try {
            this.recognition.stop();
        } catch {
            this.completeRecording();
        }
    }

    public readText(text: string): Promise<void> {
        if (typeof window === "undefined") {
            return Promise.resolve();
        }

        const normalizedText = text.trim();
        if (!normalizedText) {
            return Promise.resolve();
        }

        const requestId = ++this.playbackRequestId;

        this.cancelActivePlayback();
        this.resolveActiveReadText();

        return new Promise<void>((resolve) => {
            this.activeReadTextResolver = resolve;
            const abortController = new AbortController();
            this.activePlaybackAbortController = abortController;

            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            const authToken = this.accountService.authToken;
            if (authToken) {
                headers.Authorization = `Bearer ${authToken}`;
            }

            const finish = () => {
                if (requestId !== this.playbackRequestId) {
                    return;
                }

                this.clearActivePlayback();
                if (this.activeReadTextResolver === resolve) {
                    this.activeReadTextResolver = undefined;
                }

                resolve();
            };

            const fail = (error: unknown) => {
                console.error("TTS playback failed", error);
                finish();
            };

            void fetch(`${Environment.ApiUri}/ai/tts/stream`, {
                method: "POST",
                headers,
                body: JSON.stringify({ input: normalizedText }),
                signal: abortController.signal,
            }).then(async (response) => {
                if (!response.ok) {
                    throw new Error(`TTS request failed (${response.status} ${response.statusText}).`);
                }

                await this.playStreamingResponse(response, requestId, abortController.signal, finish);
            }).catch((error: unknown) => {
                if (abortController.signal.aborted) {
                    finish();
                    return;
                }

                fail(error);
            });
        });
    }

    public interruptRead(): void {
        this.playbackRequestId++;
        this.cancelActivePlayback();
        this.resolveActiveReadText();
    }

    private cancelActivePlayback(): void {
        if (this.activePlaybackAbortController && !this.activePlaybackAbortController.signal.aborted) {
            this.activePlaybackAbortController.abort();
        }

        this.clearActivePlayback();
    }

    private resolveActiveReadText(): void {
        if (!this.activeReadTextResolver) {
            return;
        }

        const resolve = this.activeReadTextResolver;
        this.activeReadTextResolver = undefined;
        resolve();
    }

    private clearActivePlayback(): void {
        this.activePlaybackAbortController = undefined;

        const playback = this.activeStreamingPlayback;
        this.activeStreamingPlayback = undefined;

        if (playback) {
            for (const sourceNode of playback.sourceNodes) {
                sourceNode.onended = null;
                try {
                    sourceNode.stop();
                } catch {
                    // Ignore source nodes that have already completed.
                }
                sourceNode.disconnect();
            }

            playback.sourceNodes.clear();
            playback.gainNode.disconnect();
            void playback.audioContext.close().catch(() => {
                // Ignore cleanup failures when the context is already closed.
            });
        }
    }

    private async playStreamingResponse(
        response: Response,
        requestId: number,
        abortSignal: AbortSignal,
        finish: () => void,
    ): Promise<void> {
        if (!response.body) {
            throw new Error("TTS response did not include a readable stream.");
        }

        const reader = response.body.getReader();
        let bufferedBytes: ByteArray = new Uint8Array(0);
        let wavFormat: WavFormat | undefined;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                if (requestId !== this.playbackRequestId || abortSignal.aborted || !value) {
                    finish();
                    return;
                }

                bufferedBytes = this.concatUint8Arrays(bufferedBytes, value);

                if (!wavFormat) {
                    wavFormat = this.tryParseWavFormat(bufferedBytes);
                    if (!wavFormat) {
                        continue;
                    }

                    const audioContext = await this.createStreamingAudioContext();
                    const gainNode = audioContext.createGain();
                    gainNode.connect(audioContext.destination);

                    this.activeStreamingPlayback = {
                        audioContext,
                        gainNode,
                        sourceNodes: new Set<AudioBufferSourceNode>(),
                        scheduledTime: audioContext.currentTime + 0.05,
                        pendingSourceCount: 0,
                        streamCompleted: false,
                    };

                    bufferedBytes = bufferedBytes.slice(wavFormat.dataOffset);
                }

                bufferedBytes = this.enqueueBufferedAudio(bufferedBytes, wavFormat, requestId, false, finish);
            }

            if (!wavFormat) {
                finish();
                return;
            }

            bufferedBytes = this.enqueueBufferedAudio(bufferedBytes, wavFormat, requestId, true, finish);
            const playback = this.activeStreamingPlayback;
            if (!playback) {
                finish();
                return;
            }

            playback.streamCompleted = true;
            if (playback.pendingSourceCount === 0) {
                finish();
            }
        } finally {
            reader.releaseLock();
        }
    }

    private async createStreamingAudioContext(): Promise<AudioContext> {
        const browserWindow = window as BrowserWindow & {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
        };
        const AudioContextConstructor = browserWindow.AudioContext ?? browserWindow.webkitAudioContext;
        if (!AudioContextConstructor) {
            throw new Error("Web Audio is not supported by this browser.");
        }

        const audioContext = new AudioContextConstructor();
        if (audioContext.state === "suspended") {
            await audioContext.resume();
        }

        return audioContext;
    }

    private enqueueBufferedAudio(
        bufferedBytes: ByteArray,
        wavFormat: WavFormat,
        requestId: number,
        flush: boolean,
        finish: () => void,
    ): ByteArray {
        const playback = this.activeStreamingPlayback;
        if (!playback || requestId !== this.playbackRequestId) {
            finish();
            return new Uint8Array(0);
        }

        const totalFrames = Math.floor(bufferedBytes.length / wavFormat.blockAlign);
        if (totalFrames <= 0) {
            return bufferedBytes;
        }

        const frameBatchSize = flush ? totalFrames : totalFrames - (totalFrames % this.minStreamingFrames);
        if (frameBatchSize <= 0) {
            return bufferedBytes;
        }

        const bytesToSchedule = frameBatchSize * wavFormat.blockAlign;
        const scheduledBytes = bufferedBytes.slice(0, bytesToSchedule);
        this.schedulePcmPlayback(playback, wavFormat, scheduledBytes, requestId, finish);
        return bufferedBytes.slice(bytesToSchedule);
    }

    private schedulePcmPlayback(
        playback: ActiveStreamingPlayback,
        wavFormat: WavFormat,
        pcmBytes: ByteArray,
        requestId: number,
        finish: () => void,
    ): void {
        const frameCount = pcmBytes.length / wavFormat.blockAlign;
        if (frameCount <= 0) {
            return;
        }

        const audioBuffer = playback.audioContext.createBuffer(wavFormat.channelCount, frameCount, wavFormat.sampleRate);
        this.copyPcmToAudioBuffer(audioBuffer, pcmBytes, wavFormat);

        const sourceNode = playback.audioContext.createBufferSource();
        sourceNode.buffer = audioBuffer;
        sourceNode.connect(playback.gainNode);

        const startTime = Math.max(playback.scheduledTime, playback.audioContext.currentTime + 0.03);
        playback.scheduledTime = startTime + audioBuffer.duration;
        playback.pendingSourceCount++;
        playback.sourceNodes.add(sourceNode);

        sourceNode.onended = () => {
            playback.sourceNodes.delete(sourceNode);
            sourceNode.disconnect();
            playback.pendingSourceCount = Math.max(0, playback.pendingSourceCount - 1);

            if (this.activeStreamingPlayback !== playback || requestId !== this.playbackRequestId) {
                return;
            }

            if (playback.streamCompleted && playback.pendingSourceCount === 0) {
                finish();
            }
        };

        sourceNode.start(startTime);
    }

    private copyPcmToAudioBuffer(audioBuffer: AudioBuffer, pcmBytes: ByteArray, wavFormat: WavFormat): void {
        const bytesPerSample = wavFormat.bitsPerSample / 8;
        const frameCount = pcmBytes.length / wavFormat.blockAlign;
        const dataView = new DataView(pcmBytes.buffer, pcmBytes.byteOffset, pcmBytes.byteLength);

        for (let channelIndex = 0; channelIndex < wavFormat.channelCount; channelIndex++) {
            const channelData = audioBuffer.getChannelData(channelIndex);

            for (let frameIndex = 0; frameIndex < frameCount; frameIndex++) {
                const sampleOffset = frameIndex * wavFormat.blockAlign + channelIndex * bytesPerSample;
                channelData[frameIndex] = this.readPcmSample(dataView, sampleOffset, wavFormat.audioFormat, wavFormat.bitsPerSample);
            }
        }
    }

    private readPcmSample(dataView: DataView, offset: number, audioFormat: number, bitsPerSample: number): number {
        if (audioFormat === 3 && bitsPerSample === 32) {
            return dataView.getFloat32(offset, true);
        }

        if (audioFormat !== 1) {
            throw new Error(`Unsupported WAV audio format: ${audioFormat}.`);
        }

        switch (bitsPerSample) {
            case 8:
                return (dataView.getUint8(offset) - 128) / 128;
            case 16:
                return dataView.getInt16(offset, true) / 32768;
            case 24: {
                const sample = (
                    (dataView.getUint8(offset + 2) << 24)
                    | (dataView.getUint8(offset + 1) << 16)
                    | (dataView.getUint8(offset) << 8)
                ) >> 8;
                return sample / 8388608;
            }
            case 32:
                return dataView.getInt32(offset, true) / 2147483648;
            default:
                throw new Error(`Unsupported WAV bit depth: ${bitsPerSample}.`);
        }
    }

    private tryParseWavFormat(bytes: ByteArray): WavFormat | undefined {
        if (bytes.length < 12) {
            return undefined;
        }

        const dataView = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        if (this.readFourCc(dataView, 0) !== "RIFF" || this.readFourCc(dataView, 8) !== "WAVE") {
            throw new Error("Unsupported TTS stream format. Expected WAV audio.");
        }

        let offset = 12;
        let audioFormat: number | undefined;
        let channelCount: number | undefined;
        let sampleRate: number | undefined;
        let bitsPerSample: number | undefined;
        let blockAlign: number | undefined;

        while (offset + 8 <= bytes.length) {
            const chunkId = this.readFourCc(dataView, offset);
            const chunkSize = dataView.getUint32(offset + 4, true);
            const chunkDataOffset = offset + 8;
            const nextChunkOffset = chunkDataOffset + chunkSize + (chunkSize % 2);

            if (chunkId === "fmt ") {
                if (chunkDataOffset + chunkSize > bytes.length || chunkSize < 16) {
                    return undefined;
                }

                audioFormat = dataView.getUint16(chunkDataOffset, true);
                channelCount = dataView.getUint16(chunkDataOffset + 2, true);
                sampleRate = dataView.getUint32(chunkDataOffset + 4, true);
                blockAlign = dataView.getUint16(chunkDataOffset + 12, true);
                bitsPerSample = dataView.getUint16(chunkDataOffset + 14, true);
            }

            if (chunkId === "data") {
                if (!audioFormat || !channelCount || !sampleRate || !bitsPerSample || !blockAlign) {
                    throw new Error("Invalid WAV stream. Missing format metadata before data chunk.");
                }

                return {
                    audioFormat,
                    channelCount,
                    sampleRate,
                    bitsPerSample,
                    blockAlign,
                    dataOffset: chunkDataOffset,
                };
            }

            if (nextChunkOffset > bytes.length) {
                return undefined;
            }

            offset = nextChunkOffset;
        }

        return undefined;
    }

    private readFourCc(dataView: DataView, offset: number): string {
        return String.fromCharCode(
            dataView.getUint8(offset),
            dataView.getUint8(offset + 1),
            dataView.getUint8(offset + 2),
            dataView.getUint8(offset + 3),
        );
    }

    private concatUint8Arrays(left: ByteArray, right: ByteArray): ByteArray {
        if (left.length === 0) {
            return new Uint8Array(right);
        }

        const combined = new Uint8Array(left.length + right.length);
        combined.set(left);
        combined.set(right, left.length);
        return combined;
    }

    private completeRecording(): void {
        this.recordingSubject?.complete();
        this.cleanupState();
    }

    private emitRecordingError(error: Error): void {
        this.recordingSubject?.error(error);
        this.cleanupState();
    }

    private cleanupState(): void {
        this.shouldKeepRecording = false;
        this.recordingLanguage = "en-US";
        this.committedTranscript = "";
        this.resetSessionResults();
        this.lastEmittedTranscript = "";
        this.recordingSubject = undefined;
        this.forceStopCurrentRecognition();
    }

    private forceStopCurrentRecognition(): void {
        this.clearPendingRestart();

        if (!this.recognition) {
            return;
        }

        const recognition = this.recognition;
        this.recognition = undefined;

        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;

        try {
            recognition.abort();
        } catch {
            // Ignore cleanup errors from stale recognition instances.
        }
    }

    private startRecognitionSession(): void {
        const speechRecognitionConstructor = this.getSpeechRecognitionConstructor();
        if (!speechRecognitionConstructor) {
            throw new Error("Speech recognition is not supported by this browser.");
        }

        this.resetSessionResults();

        const recognition = new speechRecognitionConstructor();
        this.recognition = recognition;

        recognition.lang = "en-US";
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
            if (this.recognition !== recognition) {
                return;
            }

            this.syncSessionResults(event);
            this.emitCurrentTranscript();
        };

        recognition.onerror = (event) => {
            if (this.recognition !== recognition) {
                return;
            }

            if (!this.shouldKeepRecording && event.error === "aborted") {
                return;
            }

            if (this.shouldRecoverFromError(event.error)) {
                return;
            }

            const message = event.message ? `${event.error}: ${event.message}` : event.error;
            this.emitRecordingError(new Error(`Speech recognition failed (${message}).`));
        };

        recognition.onend = () => {
            if (this.recognition !== recognition) {
                return;
            }

            this.commitCurrentSessionTranscript();
            this.recognition = undefined;

            if (!this.shouldKeepRecording) {
                this.completeRecording();
                return;
            }

            this.scheduleRecognitionRestart();
        };

        recognition.start();
    }

    private scheduleRecognitionRestart(): void {
        this.clearPendingRestart();

        this.restartTimeoutId = window.setTimeout(() => {
            this.restartTimeoutId = undefined;

            if (!this.shouldKeepRecording || this.recognition) {
                return;
            }

            try {
                this.startRecognitionSession();
            } catch (error) {
                this.emitRecordingError(this.asError(error, "Unable to restart speech recognition."));
            }
        }, this.restartDelayMs);
    }

    private clearPendingRestart(): void {
        if (this.restartTimeoutId === undefined) {
            return;
        }

        window.clearTimeout(this.restartTimeoutId);
        this.restartTimeoutId = undefined;
    }

    private syncSessionResults(event: BrowserSpeechRecognitionEvent): void {
        const { resultIndex, results } = event;

        this.sessionResults.length = results.length;

        for (let index = resultIndex; index < results.length; index++) {
            const result = results[index];
            const transcriptSegment = this.normalizeTranscript(result?.[0]?.transcript ?? "");

            this.sessionResults[index] = {
                transcript: transcriptSegment,
                isFinal: Boolean(result?.isFinal),
            };
        }
    }

    private emitCurrentTranscript(): void {
        this.emitTranscript(this.buildCombinedTranscript(true));
    }

    private emitTranscript(transcript: string): void {
        const normalizedTranscript = this.normalizeTranscript(transcript);

        if (!normalizedTranscript || normalizedTranscript === this.lastEmittedTranscript) {
            return;
        }

        this.lastEmittedTranscript = normalizedTranscript;
        this.recordingSubject?.next(normalizedTranscript);
    }

    private commitCurrentSessionTranscript(): void {
        const currentSessionTranscript = this.buildSessionTranscript(true);

        this.committedTranscript = this.mergeSessionTranscript(this.committedTranscript, currentSessionTranscript);
        this.resetSessionResults();
        this.emitTranscript(this.committedTranscript);
    }

    private shouldRecoverFromError(error: string): boolean {
        return this.shouldKeepRecording && ["aborted", "network", "no-speech"].includes(error);
    }

    private buildCombinedTranscript(includeInterim: boolean): string {
        return this.mergeSessionTranscript(this.committedTranscript, this.buildSessionTranscript(includeInterim));
    }

    private buildSessionTranscript(includeInterim: boolean): string {
        let transcript = "";

        for (const result of this.sessionResults) {
            if (!result || (!result.isFinal && !includeInterim)) {
                continue;
            }

            transcript = this.mergeSessionTranscript(transcript, result.transcript);
        }

        return this.normalizeTranscript(transcript);
    }

    private mergeSessionTranscript(baseTranscript: string, nextTranscript: string): string {
        const normalizedBaseTranscript = this.normalizeTranscript(baseTranscript);
        const normalizedNextTranscript = this.normalizeTranscript(nextTranscript);

        if (!normalizedBaseTranscript) {
            return normalizedNextTranscript;
        }

        if (!normalizedNextTranscript) {
            return normalizedBaseTranscript;
        }

        if (normalizedBaseTranscript === normalizedNextTranscript) {
            return normalizedBaseTranscript;
        }

        if (normalizedNextTranscript.startsWith(`${normalizedBaseTranscript} `)) {
            return normalizedNextTranscript;
        }

        if (normalizedBaseTranscript.endsWith(` ${normalizedNextTranscript}`)) {
            return normalizedBaseTranscript;
        }

        const baseWords = normalizedBaseTranscript.split(" ");
        const nextWords = normalizedNextTranscript.split(" ");
        const lowerCaseBaseWords = baseWords.map((word) => word.toLowerCase());
        const lowerCaseNextWords = nextWords.map((word) => word.toLowerCase());
        const maxOverlap = Math.min(baseWords.length, nextWords.length);

        for (let overlapLength = maxOverlap; overlapLength > 0; overlapLength--) {
            const baseSuffix = lowerCaseBaseWords.slice(-overlapLength).join(" ");
            const nextPrefix = lowerCaseNextWords.slice(0, overlapLength).join(" ");

            if (baseSuffix === nextPrefix) {
                return [...baseWords, ...nextWords.slice(overlapLength)].join(" ");
            }
        }

        return `${normalizedBaseTranscript} ${normalizedNextTranscript}`;
    }

    private resetSessionResults(): void {
        this.sessionResults = [];
    }

    private normalizeTranscript(transcript: string): string {
        return transcript.replace(/\s+/g, " ").trim();
    }

    private getSpeechRecognitionConstructor() {
        if (typeof window === "undefined") {
            return undefined;
        }

        const browserWindow = window as BrowserWindow;
        return browserWindow.webkitSpeechRecognition ?? browserWindow.SpeechRecognition;
    }

    private asError(error: unknown, fallbackMessage: string): Error {
        if (error instanceof Error) {
            return error;
        }

        return new Error(fallbackMessage);
    }

}
